import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import DrawingSearchPanel from "../DesignerSearchPanel";
import DrawingSnGrid from "../DesignerSnGrid";
import DesignerSnResizable from "../DesignerSnResizable";
import { useReducer, useCallback } from "react";
import { Button } from "@mui/material";
import SnTab from "@shared/components/SnTab";

type PanelSizes = Record<string, string | number>;

interface Action {
  type: "SET_SIZE";
  key: string;
  value: string | number;
}

interface Props {
  loadData?: any;
}

const reducer = (state: PanelSizes, action: Action): PanelSizes => {
  switch (action.type) {
    case "SET_SIZE":
      return { ...state, [action.key]: action.value };
    default:
      return state;
  }
};

const usePanelSizes = (
  defaultSizes: PanelSizes
): [
  PanelSizes,
  (
    seq: number,
    size: string | number,
    options?: { skipStorage?: boolean }
  ) => void,
] => {
  const panelKeys = Object.keys(defaultSizes);

  const initialSizes: PanelSizes = {};
  panelKeys.forEach((key) => {
    initialSizes[key] = defaultSizes[key];
  });

  const [state, dispatch] = useReducer(reducer, initialSizes);

  const resize = useCallback(
    (seq: number, size: string | number) => {
      const key = panelKeys[seq - 1];
      if (!key) return;

      dispatch({ type: "SET_SIZE", key, value: size });
    },
    [panelKeys]
  );

  return [state, resize];
};

const TemplatePopup1 = forwardRef(({ loadData }: Props, ref) => {
  const gridRef1 = useRef<any>(null);
  const gridRef2 = useRef<any>(null);
  const gridRef3 = useRef<any>(null);
  const gridRef4 = useRef<any>(null);
  const searchRef = useRef<any>(null);
  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns1, setColumns1] = useState<any[]>([{}]);
  const [columns2, setColumns2] = useState<any[]>([{}]);
  const [columns3, setColumns3] = useState<any[]>([{}]);
  const [columns4, setColumns4] = useState<any[]>([{}]);
  const [gridSettings1, setGridSettings1] = useState<any[]>([{}]);
  const [gridSettings2, setGridSettings2] = useState<any[]>([{}]);
  const [gridSettings3, setGridSettings3] = useState<any[]>([{}]);
  const [gridSettings4, setGridSettings4] = useState<any[]>([{}]);
  const defaultSize = { panel1height: "49.5%", panel2height: "49.5%" };
  const [type, setType] = useState<string>("2");
  const [tabIndex, setTabIndex] = useState<string>("1");

  const [sizes, onResizeStop] = usePanelSizes(defaultSize);
  useEffect(() => {
    if (!loadData) {
      setFormData(null);
      setFormControlData([]);
      setColumns1([{}]);
      setColumns2([{}]);
      setColumns3([{}]);
      setColumns4([{}]);
    } else {
      setFormData(loadData.formData);
      setFormControlData(loadData.formControlData);
      setColumns1(loadData.grid.grid1);
      setColumns2(loadData.grid.grid2);
      if (loadData.grid.grid3) {
        setColumns3(loadData.grid.grid3);
      }
      if (loadData.grid.grid4) {
        setColumns4(loadData.grid.grid4);
      }
      if (loadData.gridSettings?.grid1)
        setGridSettings1(loadData.gridSettings.grid1);
      if (loadData.gridSettings?.grid2)
        setGridSettings2(loadData.gridSettings.grid2);
      if (loadData.gridSettings?.grid3)
        setGridSettings3(loadData.gridSettings.grid3);
      if (loadData.gridSettings?.grid4)
        setGridSettings4(loadData.gridSettings.grid4);
    }
  }, [loadData]);

  useImperativeHandle(ref, () => ({
    getSelectedRow,
  }));

  const getSelectedRow = () => {
    return getData();
  };

  const getData = async () => {
    const searchformData = await searchRef.current.getFormData();
    const searchControls = await searchRef.current.getControls();
    const gridColumns1 = await gridRef1.current.getColumns();
    const gridColumns2 = await gridRef2.current.getColumns();
    const gridColumns3 = await gridRef3.current.getColumns();
    const gridColumns4 = await gridRef4.current.getColumns();
    const gridSettings1 = await gridRef1.current.getSettings();
    const gridSettings2 = await gridRef2.current.getSettings();
    const gridSettings3 = await gridRef3.current.getSettings();
    const gridSettings4 = await gridRef4.current.getSettings();
    let rtn;
    if (type === "1") {
      rtn = {
        formData: searchformData,
        formControlData: searchControls,
        grid: {
          grid1: gridColumns1,
          grid2: gridColumns2,
        },
        gridSettings: {
          grid1: {
            ...gridSettings1,
          },
          grid2: {
            ...gridSettings2,
          },
        },
      };
    } else {
      rtn = {
        formData: searchformData,
        formControlData: searchControls,
        grid: {
          grid1: gridColumns1,
          grid2: gridColumns2,
          grid3: gridColumns3,
          grid4: gridColumns4,
        },
        gridSettings: {
          grid1: {
            ...gridSettings1,
          },
          grid2: {
            ...gridSettings2,
          },
          grid3: {
            ...gridSettings3,
          },
          grid4: {
            ...gridSettings4,
          },
        },
      };
    }
    return [rtn];
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex flex-row items-center gap-2">
        <Button
          onClick={() => {
            setType("1");
          }}
          variant="outlined"
          sx={{ width: 80 }}
        >
          탭1
        </Button>
        <Button
          onClick={() => {
            setType("2");
          }}
          variant="outlined"
          sx={{ width: 80 }}
        >
          탭2
        </Button>
      </div>
      <div className="mb-1">
        <DrawingSearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
          popupRef={ref}
          isPopup={true}
        ></DrawingSearchPanel>
      </div>

      <div className={type === "1" ? "flex-1 block h-full" : "hidden"}>
        <DesignerSnResizable
          direction={"height"}
          seq={1}
          size={sizes.panel1height}
          onResizeStop={onResizeStop}
          enable={{ bottom: true }}
          minHeight={130}
        >
          <DrawingSnGrid
            ref={gridRef1}
            seq={1}
            enableAddRow={true}
            columnConfig={columns1}
            rowData={[]}
            title="타이틀"
            settingsData={gridSettings1}
            isPopup={true}
          />
        </DesignerSnResizable>
        <DesignerSnResizable
          direction={"height"}
          seq={2}
          size={sizes.panel2height}
          onResizeStop={onResizeStop}
          enable={{ bottom: true }}
          minHeight={130}
        >
          <DrawingSnGrid
            ref={gridRef2}
            seq={2}
            enableAddRow={true}
            columnConfig={columns2}
            rowData={[]}
            settingsData={gridSettings2}
            isPopup={true}
          />
        </DesignerSnResizable>
      </div>
      <div className={type === "2" ? "flex-1 block h-full" : "hidden"}>
        <DesignerSnResizable
          direction={"height"}
          seq={1}
          size={sizes.panel1height}
          onResizeStop={onResizeStop}
          enable={{ bottom: true }}
          minHeight={130}
        >
          <SnTab
            titles={["탭1", "탭2"]}
            onTabChanged={(index) => {
              setTabIndex(index);
            }}
            body={[
              <DrawingSnGrid
                ref={gridRef1}
                seq={1}
                enableAddRow={true}
                columnConfig={columns1}
                rowData={[]}
                settingsData={gridSettings1}
                isPopup={true}
              />,
              <DrawingSnGrid
                ref={gridRef3}
                seq={3}
                enableAddRow={true}
                columnConfig={columns3}
                rowData={[]}
                settingsData={gridSettings3}
                isPopup={true}
              />,
            ]}
          ></SnTab>

          <div className={tabIndex === "1" ? "block h-full" : "hidden"}>
            <DrawingSnGrid
              ref={gridRef2}
              seq={2}
              enableAddRow={true}
              columnConfig={columns2}
              rowData={[]}
              settingsData={gridSettings2}
              isPopup={true}
            />
          </div>

          <div className={tabIndex === "2" ? "block h-full" : "hidden"}>
            <DrawingSnGrid
              ref={gridRef4}
              seq={4}
              enableAddRow={true}
              columnConfig={columns4}
              rowData={[]}
              settingsData={gridSettings4}
              isPopup={true}
            />
          </div>
        </DesignerSnResizable>
      </div>
    </div>
  );
});

export default TemplatePopup1;
